/* =========================================================
   PTAH CUSTOMIZER — Three.js apparel viewer with print upload
   ========================================================= */
(function () {
  'use strict';

  const root = document.querySelector('[data-customizer]');
  if (!root || typeof THREE === 'undefined') return;

  const viewer = root.querySelector('[data-cz-viewer]');
  const fileInput = root.querySelector('[data-cz-file]');
  const uploadBox = root.querySelector('[data-cz-upload]');
  const uploadName = root.querySelector('[data-cz-upload-name]');
  const positionTabs = root.querySelectorAll('[data-cz-position]');
  const methodBtns = root.querySelectorAll('[data-cz-method]');
  const sizeRange = root.querySelector('[data-cz-size]');
  const sizeVal = root.querySelector('[data-cz-size-val]');
  const yRange = root.querySelector('[data-cz-y]');
  const yVal = root.querySelector('[data-cz-y-val]');
  const garmentBtns = root.querySelectorAll('[data-cz-garment]');

  if (!viewer) return;

  // --- State
  const state = {
    garment: 'tee',      // tee | hoodie | sweat
    position: 'front',   // front | back | sleeve
    method: 'dtf',       // dtf | embroidery | vinyl
    printSize: 1.0,
    printY: 0,
    imageURL: null,
    imageTexture: null
  };

  // --- Three.js setup
  const w = viewer.clientWidth;
  const h = viewer.clientHeight;

  const scene = new THREE.Scene();
  scene.background = null;
  scene.fog = new THREE.Fog(0x202625, 8, 18);

  const camera = new THREE.PerspectiveCamera(35, w / h, 0.1, 100);
  camera.position.set(0, 0.4, 7.5);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(w, h);
  renderer.outputEncoding = THREE.sRGBEncoding;
  viewer.appendChild(renderer.domElement);

  // Lights: studio rim setup
  scene.add(new THREE.AmbientLight(0xffffff, 0.45));
  const keyLight = new THREE.DirectionalLight(0xffffff, 0.95);
  keyLight.position.set(3, 4, 5);
  scene.add(keyLight);
  const rimLight = new THREE.DirectionalLight(0xB59A6A, 0.35);
  rimLight.position.set(-4, 2, -3);
  scene.add(rimLight);
  const fillLight = new THREE.DirectionalLight(0xC6C6C0, 0.25);
  fillLight.position.set(-2, -1, 2);
  scene.add(fillLight);

  // --- Procedural fabric texture (washed cotton look)
  function makeFabricTexture() {
    const c = document.createElement('canvas');
    c.width = c.height = 512;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#2C2F2E';
    ctx.fillRect(0, 0, 512, 512);
    // weave noise
    for (let i = 0; i < 8000; i++) {
      const v = 0.5 + Math.random() * 0.7;
      ctx.fillStyle = `rgba(${Math.floor(40*v)},${Math.floor(43*v)},${Math.floor(42*v)},0.4)`;
      ctx.fillRect(Math.random() * 512, Math.random() * 512, 1, 1);
    }
    // horizontal yarn streaks
    for (let i = 0; i < 200; i++) {
      ctx.strokeStyle = `rgba(60,63,62,${Math.random() * 0.3})`;
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(0, Math.random() * 512);
      ctx.lineTo(512, Math.random() * 512);
      ctx.stroke();
    }
    // wash variation blobs
    for (let i = 0; i < 30; i++) {
      const x = Math.random() * 512;
      const y = Math.random() * 512;
      const r = 30 + Math.random() * 90;
      const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
      grad.addColorStop(0, 'rgba(70,73,72,0.18)');
      grad.addColorStop(1, 'rgba(70,73,72,0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(2, 2);
    return tex;
  }

  const fabricTex = makeFabricTexture();
  const fabricMat = new THREE.MeshStandardMaterial({
    color: 0x2A2E2D,
    map: fabricTex,
    roughness: 0.92,
    metalness: 0.02,
  });

  // --- Build garment geometry (simple but recognizable)
  const garmentGroup = new THREE.Group();
  scene.add(garmentGroup);

  function buildTee() {
    const group = new THREE.Group();
    // Torso — soft rounded box via ExtrudeGeometry from a shape
    const shape = new THREE.Shape();
    shape.moveTo(-1.4, -1.8);
    shape.lineTo(-1.5, 1.2);
    shape.lineTo(-1.7, 1.4);     // shoulder corner
    shape.lineTo(-2.3, 1.6);     // sleeve extension
    shape.lineTo(-2.4, 1.1);     // sleeve hem
    shape.lineTo(-1.6, 0.95);    // armpit
    shape.lineTo(-1.4, 1.0);
    shape.lineTo(-1.2, 1.55);    // neck side
    shape.bezierCurveTo(-0.6, 1.4, 0.6, 1.4, 1.2, 1.55);
    shape.lineTo(1.4, 1.0);
    shape.lineTo(1.6, 0.95);
    shape.lineTo(2.4, 1.1);
    shape.lineTo(2.3, 1.6);
    shape.lineTo(1.7, 1.4);
    shape.lineTo(1.5, 1.2);
    shape.lineTo(1.4, -1.8);
    shape.bezierCurveTo(0.7, -1.92, -0.7, -1.92, -1.4, -1.8);

    const extrudeSettings = {
      depth: 0.55,
      bevelEnabled: true,
      bevelSegments: 4,
      steps: 2,
      bevelSize: 0.12,
      bevelThickness: 0.12,
      curveSegments: 24
    };
    const geo = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    geo.translate(0, 0, -0.275);
    const mesh = new THREE.Mesh(geo, fabricMat);
    group.add(mesh);
    return group;
  }

  function buildHoodie() {
    const group = new THREE.Group();
    const shape = new THREE.Shape();
    shape.moveTo(-1.55, -2.0);
    shape.lineTo(-1.65, 1.1);
    shape.lineTo(-1.9, 1.35);
    shape.lineTo(-2.55, 1.65);   // sleeve outer
    shape.lineTo(-2.7, 1.0);
    shape.lineTo(-1.8, 0.85);
    shape.lineTo(-1.6, 0.95);
    shape.lineTo(-1.3, 1.6);     // shoulder up to hood
    shape.bezierCurveTo(-0.7, 1.55, 0.7, 1.55, 1.3, 1.6);
    shape.lineTo(1.6, 0.95);
    shape.lineTo(1.8, 0.85);
    shape.lineTo(2.7, 1.0);
    shape.lineTo(2.55, 1.65);
    shape.lineTo(1.9, 1.35);
    shape.lineTo(1.65, 1.1);
    shape.lineTo(1.55, -2.0);
    shape.bezierCurveTo(0.8, -2.12, -0.8, -2.12, -1.55, -2.0);

    const extrudeSettings = { depth: 0.7, bevelEnabled: true, bevelSegments: 4, steps: 2, bevelSize: 0.14, bevelThickness: 0.14, curveSegments: 24 };
    const geo = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    geo.translate(0, 0, -0.35);
    const torso = new THREE.Mesh(geo, fabricMat);
    group.add(torso);

    // Hood (rounded shape behind shoulders)
    const hoodGeo = new THREE.SphereGeometry(0.85, 32, 24, 0, Math.PI * 2, 0, Math.PI / 1.8);
    const hood = new THREE.Mesh(hoodGeo, fabricMat);
    hood.position.set(0, 1.85, -0.05);
    hood.rotation.x = -0.2;
    hood.scale.set(1, 1.2, 0.9);
    group.add(hood);

    // Pocket pouch (front kangaroo)
    const pocketShape = new THREE.Shape();
    pocketShape.moveTo(-1.1, -0.6);
    pocketShape.lineTo(-1.1, -1.4);
    pocketShape.lineTo(1.1, -1.4);
    pocketShape.lineTo(1.1, -0.6);
    pocketShape.lineTo(0.7, -0.4);
    pocketShape.lineTo(-0.7, -0.4);
    pocketShape.lineTo(-1.1, -0.6);
    const pocketGeo = new THREE.ExtrudeGeometry(pocketShape, { depth: 0.08, bevelEnabled: true, bevelSegments: 2, bevelSize: 0.04, bevelThickness: 0.03 });
    const pocketMat = fabricMat.clone();
    pocketMat.color = new THREE.Color(0x252928);
    const pocket = new THREE.Mesh(pocketGeo, pocketMat);
    pocket.position.set(0, 0, 0.31);
    group.add(pocket);

    return group;
  }

  function buildSweat() {
    const group = new THREE.Group();
    const shape = new THREE.Shape();
    shape.moveTo(-1.55, -1.95);
    shape.lineTo(-1.65, 1.15);
    shape.lineTo(-1.9, 1.4);
    shape.lineTo(-2.5, 1.65);
    shape.lineTo(-2.65, 1.0);
    shape.lineTo(-1.8, 0.85);
    shape.lineTo(-1.6, 0.95);
    shape.lineTo(-1.25, 1.65);   // neck/shoulder
    shape.bezierCurveTo(-0.55, 1.5, 0.55, 1.5, 1.25, 1.65);
    shape.lineTo(1.6, 0.95);
    shape.lineTo(1.8, 0.85);
    shape.lineTo(2.65, 1.0);
    shape.lineTo(2.5, 1.65);
    shape.lineTo(1.9, 1.4);
    shape.lineTo(1.65, 1.15);
    shape.lineTo(1.55, -1.95);
    shape.bezierCurveTo(0.8, -2.07, -0.8, -2.07, -1.55, -1.95);

    const extrudeSettings = { depth: 0.65, bevelEnabled: true, bevelSegments: 4, steps: 2, bevelSize: 0.14, bevelThickness: 0.14, curveSegments: 24 };
    const geo = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    geo.translate(0, 0, -0.325);
    const mesh = new THREE.Mesh(geo, fabricMat);
    group.add(mesh);
    return group;
  }

  // --- Print plane (the user's uploaded image)
  const printGeo = new THREE.PlaneGeometry(1.6, 1.6);
  let printMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0,
    side: THREE.DoubleSide,
    alphaTest: 0.02,
    depthWrite: false
  });
  const printPlane = new THREE.Mesh(printGeo, printMat);
  printPlane.visible = false;
  garmentGroup.add(printPlane);

  function placePrint() {
    if (!state.imageTexture) { printPlane.visible = false; return; }
    printPlane.visible = true;
    printPlane.scale.set(state.printSize, state.printSize, 1);
    if (state.position === 'front') {
      printPlane.position.set(0, 0.1 + state.printY, 0.32);
      printPlane.rotation.set(0, 0, 0);
    } else if (state.position === 'back') {
      printPlane.position.set(0, 0.1 + state.printY, -0.32);
      printPlane.rotation.set(0, Math.PI, 0);
    } else { // sleeve
      printPlane.scale.set(state.printSize * 0.5, state.printSize * 0.5, 1);
      printPlane.position.set(-2.0, 1.15, 0.05);
      printPlane.rotation.set(0, 0, 0.1);
    }
  }

  // --- Build/rebuild garment when type changes
  function rebuildGarment() {
    // remove previous
    while (garmentGroup.children.length) {
      const c = garmentGroup.children.pop();
      if (c === printPlane) continue;
      if (c.geometry) c.geometry.dispose();
    }
    garmentGroup.add(printPlane);

    let mesh;
    if (state.garment === 'hoodie') mesh = buildHoodie();
    else if (state.garment === 'sweat') mesh = buildSweat();
    else mesh = buildTee();
    garmentGroup.add(mesh);
    placePrint();
  }

  rebuildGarment();

  // --- Manual orbit controls (mouse + touch)
  let isDragging = false;
  let prevX = 0, prevY = 0;
  let targetRotY = 0, targetRotX = 0;
  let currentRotY = 0, currentRotX = 0;

  function onDown(e) {
    isDragging = true;
    const p = e.touches ? e.touches[0] : e;
    prevX = p.clientX; prevY = p.clientY;
  }
  function onMove(e) {
    if (!isDragging) return;
    const p = e.touches ? e.touches[0] : e;
    const dx = p.clientX - prevX;
    const dy = p.clientY - prevY;
    targetRotY += dx * 0.008;
    targetRotX = Math.max(-0.4, Math.min(0.4, targetRotX + dy * 0.005));
    prevX = p.clientX; prevY = p.clientY;
  }
  function onUp() { isDragging = false; }

  viewer.addEventListener('mousedown', onDown);
  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onUp);
  viewer.addEventListener('touchstart', onDown, { passive: true });
  viewer.addEventListener('touchmove', onMove, { passive: true });
  viewer.addEventListener('touchend', onUp);

  // --- Auto-snap rotation when changing print position
  function snapTo(side) {
    if (side === 'front') targetRotY = 0;
    else if (side === 'back') targetRotY = Math.PI;
    else targetRotY = -Math.PI / 2.3;
  }

  // --- Resize
  function resize() {
    const W = viewer.clientWidth;
    const H = viewer.clientHeight;
    camera.aspect = W / H;
    camera.updateProjectionMatrix();
    renderer.setSize(W, H);
  }
  window.addEventListener('resize', resize);

  // --- Animation loop
  let idleRotation = 0;
  function tick() {
    requestAnimationFrame(tick);
    // smooth rotation
    currentRotY += (targetRotY - currentRotY) * 0.12;
    currentRotX += (targetRotX - currentRotX) * 0.12;
    if (!isDragging) {
      idleRotation += 0.0008;
      garmentGroup.rotation.y = currentRotY + Math.sin(idleRotation) * 0.04;
    } else {
      garmentGroup.rotation.y = currentRotY;
    }
    garmentGroup.rotation.x = currentRotX;
    renderer.render(scene, camera);
  }
  tick();

  // --- Image upload
  if (fileInput) {
    fileInput.addEventListener('change', (e) => {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        state.imageURL = ev.target.result;
        const loader = new THREE.TextureLoader();
        loader.load(ev.target.result, (tex) => {
          tex.encoding = THREE.sRGBEncoding;
          state.imageTexture = tex;
          // dispose previous material to avoid leak
          if (printMat.map) printMat.map.dispose();
          printMat = new THREE.MeshBasicMaterial({
            map: tex,
            transparent: true,
            side: THREE.DoubleSide,
            alphaTest: 0.02,
            depthWrite: false
          });
          printPlane.material = printMat;
          placePrint();
          if (uploadBox) uploadBox.classList.add('has-file');
          if (uploadName) uploadName.textContent = file.name.length > 22 ? file.name.slice(0, 22) + '…' : file.name;
        });
      };
      reader.readAsDataURL(file);
    });
  }

  // --- Position tabs
  positionTabs.forEach(btn => {
    btn.addEventListener('click', () => {
      positionTabs.forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      state.position = btn.dataset.czPosition;
      snapTo(state.position);
      placePrint();
    });
  });

  // --- Garment switch
  garmentBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      garmentBtns.forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      state.garment = btn.dataset.czGarment;
      rebuildGarment();
    });
  });

  // --- Method buttons (visual only — doesn't affect 3D, but influences price)
  methodBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      methodBtns.forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      state.method = btn.dataset.czMethod;
      // emit event for price recalc
      root.dispatchEvent(new CustomEvent('cz:method-change', { detail: { method: state.method } }));
    });
  });

  // --- Print size slider
  if (sizeRange) {
    sizeRange.addEventListener('input', () => {
      state.printSize = parseFloat(sizeRange.value) / 100;
      if (sizeVal) sizeVal.textContent = sizeRange.value + '%';
      placePrint();
    });
  }
  if (yRange) {
    yRange.addEventListener('input', () => {
      state.printY = parseFloat(yRange.value) / 100 * 1.2;
      if (yVal) yVal.textContent = yRange.value;
      placePrint();
    });
  }

  // --- Export current state for ATC
  window.__czExport = function () {
    return {
      garment: state.garment,
      position: state.position,
      method: state.method,
      printSize: Math.round(state.printSize * 100),
      printY: yRange ? yRange.value : 0,
      imageURL: state.imageURL,
      hasDesign: !!state.imageURL
    };
  };

  // expose state for price calc
  window.__czState = state;
})();
